import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown, ChevronRight, Globe } from 'lucide-react';
import tftLogo from '@/assets/tft-logo.png';
import { industries } from '@/data/industries';
import { useLanguage } from '@/contexts/LanguageContext';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  const navigation = [
    { title: t('Αρχική', 'Home'), href: '/' },
    {
      title: t('Εταιρεία', 'Company'),
      href: '/company',
      submenu: [
        { title: t('Ιστορία', 'History'), href: '/company/history' },
        { title: t('Άνθρωποι', 'People'), href: '/company/people' },
        { title: t('Εμπειρία', 'Experience'), href: '/company/experience' },
        { title: t('Κοινωνική Ευθύνη', 'Social Responsibility'), href: '/company/certifications' },
        { title: t('Καριέρα', 'Careers'), href: '/company/careers' }
      ]
    },
    { 
      title: t('Κλάδοι', 'Industries'), 
      href: '/industries',
      submenu: industries.map(ind => ({ title: ind.title[language], href: `/industries/${ind.slug}` }))
    },
    { title: t('Συνεργάτες', 'Partners'), href: '/partners' },
    { title: t('Υποστήριξη', 'Support'), href: '/support' },
    { title: t('Τεχνικά Φυλλάδια', 'Technical Library'), href: '/resources' },
    { title: t('Επικοινωνία', 'Contact'), href: '/contact' }
  ];

  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img src={tftLogo} alt="TFT Logo" className="h-24" width="141" height="96" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center flex-1 justify-center space-x-6">
            {navigation.filter(item => item.href !== '/contact').map((item) => (
              <div key={item.href} className="relative group">
                {item.submenu ? (
                  <>
                    <Link to={item.href}>
                      <Button
                        variant="ghost"
                        className="text-foreground hover:bg-primary hover:text-primary-foreground font-medium text-base transition-all"
                      >
                        {item.title}
                        <ChevronDown className="ml-1 h-4 w-4" />
                      </Button>
                    </Link>
                    <div className="absolute left-0 top-full hidden w-64 pt-2 group-hover:block z-50">
                      <div className="rounded-xl border bg-card p-2 shadow-lg">
                        {item.title === t('Κλάδοι', 'Industries') ? (
                          // Industries with nested subcategories
                          industries.map((industry) => (
                            <div key={industry.slug} className="relative group/sub">
                              <Link
                                to={`/industries/${industry.slug}`}
                                className="flex items-center justify-between px-4 py-3 text-sm rounded-lg hover:bg-primary hover:text-primary-foreground transition-colors"
                              >
                                <span>{industry.title[language]}</span>
                                {industry.subcategories.length > 0 && (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </Link>
                              {industry.subcategories.length > 0 && (
                                <div className="absolute left-full top-0 hidden w-48 -ml-2 pl-2 group-hover/sub:block z-50">
                                  <div className="rounded-xl border bg-card p-2 shadow-lg">
                                    {industry.subcategories.map((sub) => (
                                      <Link
                                        key={sub.id}
                                        to={`/industries/${industry.slug}/${sub.slug}`}
                                        className="block px-4 py-3 text-sm rounded-lg hover:bg-primary hover:text-primary-foreground transition-colors"
                                      >
                                        {sub.title[language]}
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          // Regular submenu items
                          item.submenu.map((subitem) => (
                            <Link
                              key={subitem.href}
                              to={subitem.href}
                              className="block px-4 py-2 text-sm rounded-lg hover:bg-primary hover:text-primary-foreground transition-colors"
                            >
                              {subitem.title}
                            </Link>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={`text-foreground hover:bg-primary hover:text-primary-foreground font-medium text-base transition-all px-4 py-2 rounded-md ${
                      location.pathname === item.href ? 'text-primary' : ''
                    }`}
                  >
                    {item.title}
                  </Link>
                )}
              </div>
            ))}
          </div>
          
          {/* Language Toggle & Contact Button */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLanguage(language === 'el' ? 'en' : 'el')}
              className="text-foreground hover:bg-primary hover:text-primary-foreground transition-all gap-1"
              title={language === 'el' ? 'Switch to English' : 'Αλλαγή σε Ελληνικά'}
            >
              <Globe className="h-4 w-4" />
              <span className="text-sm font-semibold">{language === 'el' ? 'EN' : 'ΕΛ'}</span>
            </Button>

            <Button variant="default" size="lg" asChild>
              <Link to="/contact">{t('Επικοινωνία', 'Contact')}</Link>
            </Button>
          </div>

          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 space-y-2">
            {navigation.map((item) => (
              <div key={item.href}>
                <Link
                  to={item.href}
                  className="block px-4 py-2 text-foreground hover:bg-primary hover:text-primary-foreground rounded-lg"
                  onClick={() => setIsOpen(false)}
                >
                  {item.title}
                </Link>
                {item.submenu && (
                  <div className="pl-4 space-y-1 mt-1">
                    {item.title === t('Κλάδοι', 'Industries') ? (
                      // Industries with nested subcategories for mobile
                      industries.map((industry) => (
                        <div key={industry.slug}>
                          <Link
                            to={`/industries/${industry.slug}`}
                            className="block px-4 py-2 text-sm text-muted-foreground hover:bg-primary hover:text-primary-foreground rounded-lg"
                            onClick={() => setIsOpen(false)}
                          >
                            {industry.title[language]}
                          </Link>
                          {industry.subcategories.length > 0 && (
                            <div className="pl-4 space-y-1">
                              {industry.subcategories.map((sub) => (
                                <Link
                                  key={sub.id}
                                  to={`/industries/${industry.slug}/${sub.slug}`}
                                  className="block px-4 py-1 text-xs text-muted-foreground hover:bg-primary hover:text-primary-foreground rounded-lg"
                                  onClick={() => setIsOpen(false)}
                                >
                                  • {sub.title[language]}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      // Regular submenu items for mobile
                      item.submenu.map((subitem) => (
                        <Link
                          key={subitem.href}
                          to={subitem.href}
                          className="block px-4 py-2 text-sm text-muted-foreground hover:bg-primary hover:text-primary-foreground rounded-lg"
                          onClick={() => setIsOpen(false)}
                        >
                          {subitem.title}
                        </Link>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))}
            
            {/* Mobile Language Toggle */}
            <Button
              variant="outline"
              className="w-full mt-4"
              onClick={() => setLanguage(language === 'el' ? 'en' : 'el')}
            >
              <Globe className="h-5 w-5 mr-2" />
              {language === 'el' ? 'Switch to English' : 'Αλλαγή σε Ελληνικά'}
            </Button>

            <Button variant="default" size="lg" className="w-full" asChild>
              <Link to="/contact" onClick={() => setIsOpen(false)}>
                {t('Επικοινωνήστε', 'Contact Us')}
              </Link>
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;